import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "text-white text-overline" }
const _hoisted_2 = { class: "row justify-center items-center full-width" }
const _hoisted_3 = {
  key: 0,
  class: "q-pa-md fit flex flex-center"
}
const _hoisted_4 = {
  key: 0,
  class: "bg-white fit rounded-borders flex flex-center"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "row justify-evenly text-center q-pt-lg" }
const _hoisted_7 = { class: "q-gutter-y-md" }
const _hoisted_8 = { class: "q-gutter-y-md" }

import { BarcodeFormat } from "@zxing/browser"
import { Result } from "@zxing/library"
import { Platform, useQuasar } from "quasar"
import { DeepReadonlyObject } from "rxdb"
import { Subscription } from "rxjs"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxScannerBarcode from "src/components/scanners/SfxScannerBarcode.vue"
import SfxScannerKeyboard from "src/components/scanners/SfxScannerKeyboard.vue"
import SfxScannerNfc from "src/components/scanners/SfxScannerNfc.vue"
import SfxSeparator from "src/components/special/SfxSeparator.vue"
import SfxToggleSettings from "src/components/toggle/SfxToggleSettings.vue"
import { AppBaseDB, getDB } from "src/db/databases"
import router from "src/router"
import { useScanStore } from "src/stores/scanStore"
import { useTopNavigationStore } from "src/stores/topNavigationStore"
import { Log } from "src/util/classes/log"
import { ContextSource } from "src/util/enums/contextSource"
import { ContextAction } from "src/util/enums/entityType"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NamedPath } from "src/util/enums/namedPaths"
import { ScanType } from "src/util/enums/scanType"
import {
  alreadyAssociatedIdentifierDialog,
  unknownIdentifierDialog,
  wrongIdentifierDialog,
} from "src/util/functions/dialogs"
import { helpTourNotImplemented } from "src/util/functions/helpTour"
import {
  getIdentifierAssociationType,
  getIdentifierEntityName,
} from "src/util/functions/identifier"
import {
  showErrorNotification,
  showNotImplementedNotification,
  showSuccessNotification,
} from "src/util/functions/notifications"
import {
  Identifier,
  IdentifierActionReturn,
  IdentifierAdd,
  IdentifierEntityTarget,
  IdentifierScan,
  IdentifierScanAction,
  IdentifierType,
} from "src/util/types/dataModels/identifier"
import { computed, onMounted, onUnmounted, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

const nfcScan = "NFC"
const qrScan = "QR"
const CAMERA_TURN_OFF_DELAY = 500 // ms
const textStyles = "flex-grow: 1; flex-basis: 0"


export default /*#__PURE__*/_defineComponent({
  __name: 'IdentifyOverlay',
  props: {
  scanAction: {
    type: String as PropType<IdentifierScanAction>,
    required: true,
  },

  identifierEntity: {
    type: Object as PropType<IdentifierEntityTarget>,
  },
},
  emits: ["dialog-ok", "dialog-hide"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const topNavigationStore = useTopNavigationStore()
const scanStore = useScanStore()
let subscription: Subscription

const $q = useQuasar()
const { t } = useI18n()

const scanTypeToggle = ref(nfcScan)
const nfcEnabled = ref(true)
const cameraActiveStatus = ref(false)
const pauseScanning = ref(false)

let db: AppBaseDB

const aboveBarcode = ref<HTMLDivElement | undefined>()
const belowBarcode = ref<HTMLDivElement | undefined>()

const bluetoothClass = computed(() => ($q.screen.lt.md ? "q-py-sm" : "q-py-md"))

const barcodeStyle = computed(() => {
  const aboveBarcodeHeight = aboveBarcode.value?.offsetHeight || 0
  const belowBarcodeHeight = belowBarcode.value?.offsetHeight || 0

  // header + padding and margins + things around the barcode
  const magicNumber = 124 + aboveBarcodeHeight + belowBarcodeHeight

  return `height: ${$q.screen.height - magicNumber}px`
})

const isSimpleScan = computed(() => props.scanAction === "SCAN")

//endregion

//region Lifecycle

onMounted(async () => {
  db = await getDB()

  subscription = topNavigationStore.overlayContextActionClicked.subscribe((contextAction) => {
    if (
      contextAction.source === ContextSource.IDENTIFY &&
      contextAction.item.action === ContextAction.HELP
    ) {
      return helpTourNotImplemented("IdentifyOverlay")
    }
  })
})

onUnmounted(() => {
  subscription?.unsubscribe()
})

//endregion

//region Functions

async function getDataFromScan(value: Result | string, type: ScanType) {
  // We have "paused" the scanning, so do nothing
  if (pauseScanning.value) return

  let fixedValue: string
  let fixedType: IdentifierType

  if (type === ScanType.CAMERA) {
    value = value as Result
    fixedValue = value.getText()

    // If it is QR, the assign it as such, otherwise assume it is of type barcode
    fixedType = value.getBarcodeFormat() === BarcodeFormat.QR_CODE ? "qr" : "barcode"
  } else if (type === ScanType.NFC) {
    fixedType = "rfid"
    fixedValue = value as string
  } else {
    // This is for handling the external keyboard reader
    // TODO: Can we be sure that this is going to be proper data?
    fixedType = "barcode"
    fixedValue = value as string
  }

  const scanData: IdentifierScan = { type: fixedType, value: fixedValue }

  return handleScanAction(props.scanAction, scanData, props.identifierEntity)
}

async function handleScanAction(
  action: IdentifierScanAction,
  scanData: IdentifierScan,
  entity?: IdentifierEntityTarget
) {
  switch (action) {
    case "ASSOCIATE":
    case "ASSOCIATION_CHANGE":
    case "PROPOSE": {
      if (entity) return associateIdentifier(scanData, entity)
      else Log.error({ message: "Empty entity during scan", data: { entity, scanData, action } })
      break
    }

    case "SCAN":
      return goToIdentifier(scanData)

    case "DISASSOCIATE":
      return dissassociateIdentifier()

    case "VERIFY": {
      if (entity) return verifyIdentifier(scanData, entity)
      else Log.error({ message: "Empty entity during scan", data: { entity, scanData, action } })
      break
    }
  }
}

async function dissassociateIdentifier() {
  // Pretty sure we might not need this scenario, but I am adding it for consistency with the other types
  throw new Error("Not implemented")
}

async function verifyIdentifier(scanData: IdentifierScan, entity: IdentifierEntityTarget) {
  const foundIdentifier = await db.identifier.getScannedIdentifier(scanData.value, scanData.type)

  if (!foundIdentifier) return unknownIdentifierScanned()

  // We founnd an identifier, but it doesn't belong to the entity we are looking for
  if (foundIdentifier[entity.entityType] !== entity.entityId) return wrongIdentifierDialog()

  const returnData: IdentifierActionReturn<"VERIFY"> = {
    action: "VERIFY",
    data: foundIdentifier.toMutableJSON(),
  }

  emit("dialog-ok", returnData)
}

async function associateIdentifier(
  scanData: IdentifierScan,
  identifierAddSource: IdentifierEntityTarget
) {
  pauseScanning.value = true

  // Always assume that this is a new identifier, and change only if there exists another
  let identifierAddType: IdentifierAdd = "PROPOSE"

  const foundIdentifier = await db.identifier.getScannedIdentifier(scanData.value, scanData.type)

  if (foundIdentifier) {
    const canChange = await canChangeAlreadyExistingIdentifier(foundIdentifier, identifierAddSource)

    pauseScanning.value = false

    // User cancelled
    if (!canChange) return

    identifierAddType = "ASSOCIATION_CHANGE"
  }

  const returnData: IdentifierActionReturn<IdentifierAdd> = {
    action: identifierAddType,
    data: scanData,
    oldAssociation: foundIdentifier,
  }

  pauseScanning.value = false

  // This will close the dialog
  emit("dialog-ok", returnData)
}

async function canChangeAlreadyExistingIdentifier(
  foundIdentifier: Identifier,
  identifierAddSource: IdentifierEntityTarget
): Promise<boolean> {
  // We have scanned data that belongs to this item
  if (foundIdentifier[identifierAddSource.entityType] === identifierAddSource.entityId) {
    showSuccessNotification(t("identifyOverlay.identify_same_entity"))
    return false
  } else {
    const oldEntityType = getIdentifierAssociationType(foundIdentifier)
    const oldEntityName = (await getIdentifierEntityName(foundIdentifier)) || ""
    const canChange = await alreadyAssociatedIdentifierDialog(
      oldEntityType,
      oldEntityName,
      identifierAddSource.entityType,
      identifierAddSource.entityName
    )

    return canChange || false
  }
}

async function goToIdentifier(scanData: IdentifierScan) {
  const foundIdentifier = await db.identifier.getScannedIdentifier(scanData.value, scanData.type)

  return foundIdentifier
    ? await scanDataRouting(foundIdentifier, scanData)
    : await unknownIdentifierScanned()
}

async function scanDataRouting(
  identifier: DeepReadonlyObject<Identifier>,
  scanData: IdentifierScan
) {
  scanStore.setIdentifier(identifier)

  if (identifier.item) {
    await router.push({ name: NamedPath.ITEM, params: { itemId: identifier.item } })
  } else if (identifier.area) {
    await router.push({ name: NamedPath.AREA, params: { areaId: identifier.area } })
  } else if (identifier.location) {
    await router.push({ name: NamedPath.LOCATION, params: { locationId: identifier.location } })
  } else {
    showErrorNotification(t("identifyOverlay.error_routing"))

    Log.error({
      message: "Identify without any entity association found",
      data: { scanData: JSON.stringify(scanData) },
    })
  }

  // Make sure to close this dialog
  emit("dialog-hide")
}

async function unknownIdentifierScanned() {
  pauseScanning.value = true
  await unknownIdentifierDialog()
  pauseScanning.value = false
}

function setNfcEnabled(nfcStatus: boolean) {
  nfcEnabled.value = nfcStatus
}

function cameraIsOff() {
  // We need to wait for a bit since the camera is still not released,
  // and on some devices we cannot use both NFC and camera at once.
  setTimeout(() => {
    if (scanTypeToggle.value === nfcScan) {
      cameraActiveStatus.value = false
    }
  }, CAMERA_TURN_OFF_DELAY)
}

async function goToItems() {
  await router.push({ name: NamedPath.SELECT_ITEM })
  emit("dialog-hide")
}

async function goToAreas() {
  showNotImplementedNotification()

  // TODO: Uncomment when we have Areas
  // await router.push({ name: NamedPath.SELECT_AREA })
  // emit("dialog-hide")
}

// endregion

//region Setup Code

watch(
  () => scanTypeToggle.value,
  async (scanValNew) => {
    if (scanValNew === qrScan) {
      cameraActiveStatus.value = true
    }
  }
)
//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "column justify-center items-center",
      ref_key: "aboveBarcode",
      ref: aboveBarcode
    }, [
      _createElementVNode("span", null, _toDisplayString(_ctx.$t("identifyOverlay.scan")), 1),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("span", {
          style: textStyles,
          class: "text-right"
        }, _toDisplayString(_ctx.$t("identifyOverlay.nfc")), 1),
        _createVNode(SfxToggleSettings, {
          modelValue: scanTypeToggle.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((scanTypeToggle).value = $event)),
          "true-value": qrScan,
          "false-value": nfcScan
        }, null, 8, ["modelValue"]),
        _createElementVNode("span", { style: textStyles }, _toDisplayString(_ctx.$t("identifyOverlay.qr")), 1)
      ])
    ], 512),
    _createElementVNode("div", {
      style: _normalizeStyle([barcodeStyle.value, {"min-height":"200px"}])
    }, [
      (scanTypeToggle.value === nfcScan && !cameraActiveStatus.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (nfcEnabled.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createVNode(_component_q_icon, {
                    name: _unref(Icons).NFC,
                    class: "text-fun-label-and-icon",
                    size: "150px"
                  }, null, 8, ["name"])
                ]))
              : _createCommentVNode("", true),
            (_unref(Platform).is.capacitor)
              ? (_openBlock(), _createBlock(SfxScannerNfc, {
                  key: 1,
                  onNfcEnabled: setNfcEnabled,
                  onPassData: _cache[1] || (_cache[1] = (value) => getDataFromScan(value, _unref(ScanType).NFC))
                }))
              : _createCommentVNode("", true)
          ]))
        : (scanTypeToggle.value === qrScan)
          ? (_openBlock(), _createBlock(SfxScannerBarcode, {
              key: 1,
              onDecode: _cache[2] || (_cache[2] = (value) => getDataFromScan(value, _unref(ScanType).CAMERA)),
              onCameraStop: cameraIsOff
            }))
          : _createCommentVNode("", true)
    ], 4),
    _createVNode(SfxScannerKeyboard, {
      onRead: _cache[3] || (_cache[3] = (value) => getDataFromScan(value, _unref(ScanType).EXTERNAL))
    }),
    _createElementVNode("div", {
      ref_key: "belowBarcode",
      ref: belowBarcode
    }, [
      _createVNode(SfxSeparator, {
        dimmed: "",
        "text-white": ""
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("identifyOverlay.or")), 1)
        ]),
        _: 1
      }),
      _createElementVNode("div", {
        class: _normalizeClass(["row justify-center items-center", bluetoothClass.value])
      }, [
        _createVNode(_component_q_icon, {
          name: _unref(Icons).BLUETOOTH,
          class: "q-px-sm",
          size: "xs"
        }, null, 8, ["name"]),
        _createElementVNode("span", null, _toDisplayString(_ctx.$t("identifyOverlay.bluetooth_scan")), 1)
      ], 2),
      (isSimpleScan.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(SfxSeparator, {
              dimmed: "",
              "text-white": ""
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("identifyOverlay.or")), 1)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(SfxBtnOutline, {
                  icon: _unref(Icons).ITEMS,
                  class: "bg-white",
                  onClick: goToItems
                }, null, 8, ["icon"]),
                _createElementVNode("p", null, _toDisplayString(_ctx.$t("identifyOverlay.find_item")), 1)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(SfxBtnOutline, {
                  icon: _unref(Icons).AREAS,
                  class: "bg-white",
                  onClick: goToAreas,
                  disable: ""
                }, null, 8, ["icon"]),
                _createElementVNode("p", null, _toDisplayString(_ctx.$t("identifyOverlay.find_area")), 1)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ], 512)
  ]))
}
}

})